import { useEffect, useState, useRef, useCallback } from 'react'
import { getConnectionStatus, type ConnectionStatusResponse } from '@/api/onboarding'

const POLL_INTERVAL_MS = 2000

export function useOnboardingPolling(token: string | null) {
  const [status, setStatus] = useState<ConnectionStatusResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [polling, setPolling] = useState(false)
  const stoppedRef = useRef(false)
  const tokenRef = useRef(token)
  tokenRef.current = token

  const fetchStatus = useCallback(async (silent = false) => {
    const t = tokenRef.current
    if (!t) return null
    if (!silent) setPolling(true)
    try {
      const res = await getConnectionStatus(t)
      setStatus(res)
      setError(null)
      if (res.state === 'rights_ok' || res.state === 'expired') {
        stoppedRef.current = true
      }
      return res
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Network error')
      return null
    } finally {
      if (!silent) setPolling(false)
    }
  }, [])

  useEffect(() => {
    if (!token) return
    stoppedRef.current = false
    let timer: number | null = null
    let cancelled = false

    async function poll() {
      if (cancelled || stoppedRef.current) return
      if (typeof document !== 'undefined' && document.hidden) {
        timer = window.setTimeout(poll, POLL_INTERVAL_MS)
        return
      }
      await fetchStatus(true)
      if (!cancelled && !stoppedRef.current) {
        timer = window.setTimeout(poll, POLL_INTERVAL_MS)
      }
    }

    function onVisibilityChange() {
      if (!document.hidden && !stoppedRef.current && !cancelled) {
        if (timer !== null) window.clearTimeout(timer)
        poll()
      }
    }

    poll()
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      cancelled = true
      stoppedRef.current = true
      if (timer !== null) window.clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [token, fetchStatus])

  return { status, error, polling, forcePoll: () => fetchStatus(false) }
}
